/**
 * ActivityCard (Sprint 4.3.3, PROD-652).
 *
 * Generic VERB-structure card for a runtime activity. Per the Awell
 * orchestration domain model:
 *
 *   "Activities use a generic structure (subject + action + object +
 *    indirect object) to describe any action that needs to be performed
 *    by a human or system."
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/
 *   api-reference/overview/domain-model.md
 *
 * Visual structure: `[icon] {subject} {action} {object} -> {indirect}`.
 * Examples:
 *   - "Awell Care Pathway Engine assigned PHQ-9 baseline -> Khalid
 *      Al-Mansoori" (form, indirect_object = patient)
 *   - "Cardiometabolic Pathway scheduled GSRS dispatch -> patient app"
 *      (reminder, indirect_object = stakeholder)
 *   - "Dr. Al-Dhaheri approved NBA candidate (TSH order)" (no indirect
 *      object; rendered as `subject action object` only)
 *
 * Used inside `<ActivityInspector>` (Sprint 4.3.2 already places this
 * card at the top of the focused-activity panel) and on
 * `<PatientPathwayTimeline>` rows so the same VERB shape renders in
 * both surfaces.
 *
 * HC2 token-only: every color resolves through Signal DS Tailwind
 * tokens. The icon inherits `currentColor` from the surrounding
 * `text-text-secondary` so the icon hue tracks the card hue.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: card is `role="article"` with an `aria-label` summarising
 * the VERB sentence so screen-reader users hear the full sentence in
 * one announcement.
 *
 * Brand-Patch v2: clinical pathway names everywhere user-facing. The
 * card is fully prop-driven so callers must pass clinical names
 * (`Cardiometabolic Pathway`) rather than internal version ids
 * (`tlp1-glp1-v1`).
 */
import {
  ACTIVITY_TYPE_LABELS,
  ActivityTypeIcon,
  type ActivityIconSize,
  type ActivityIconType,
} from './icons/ActivityTypeIcon';

export interface ActivityCardSubject {
  name: string;
  /** Optional categorical label for assistive context (e.g. "system", "practitioner"). */
  type?: string;
}

export interface ActivityCardObject {
  name: string;
  /** Optional categorical label, e.g. `'form'`, `'api_call'`. */
  type?: string;
}

export interface ActivityCardIndirectObject {
  name: string;
  /** `'patient'` or `'stakeholder'` per the Awell ontology. */
  type?: 'patient' | 'stakeholder' | 'system';
}

export interface ActivityCardProps {
  /** Closed activity-type vocabulary; selects the icon. */
  activityType: ActivityIconType;
  /** Subject performing the action. */
  subject: ActivityCardSubject;
  /** Action verb (past tense, e.g. "assigned", "dispatched"). */
  action: string;
  /** Direct object the action operated on. */
  object: ActivityCardObject;
  /**
   * Optional indirect object (assignee). Omitted when the activity is
   * assigned to the runtime system itself (Awell ontology).
   */
  indirectObject?: ActivityCardIndirectObject;
  /** Icon size; defaults to `md` (20px). */
  iconSize?: ActivityIconSize;
  className?: string;
}

/**
 * Build the canonical VERB sentence used for both the visible label and
 * the aria-label. Pure helper exported for tests + downstream consumers.
 */
export function formatVerbSentence(props: {
  subject: ActivityCardSubject;
  action: string;
  object: ActivityCardObject;
  indirectObject?: ActivityCardIndirectObject;
}): string {
  const head = `${props.subject.name} ${props.action} ${props.object.name}`;
  if (props.indirectObject !== undefined) {
    return `${head} -> ${props.indirectObject.name}`;
  }
  return head;
}

export function ActivityCard({
  activityType,
  subject,
  action,
  object,
  indirectObject,
  iconSize = 'md',
  className,
}: ActivityCardProps) {
  const sentence = formatVerbSentence({ subject, action, object, indirectObject });
  const ariaLabel = `Activity, ${ACTIVITY_TYPE_LABELS[activityType]}: ${sentence}`;
  return (
    <article
      role="article"
      aria-label={ariaLabel}
      data-testid="activity-card"
      data-activity-type={activityType}
      data-has-indirect-object={indirectObject !== undefined ? 'true' : 'false'}
      className={[
        'flex items-start gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-gutter-sm text-signal-sm text-text-primary',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        data-testid="activity-card-icon-slot"
        className="mt-0.5 text-text-secondary"
      >
        <ActivityTypeIcon type={activityType} size={iconSize} />
      </span>
      <div className="flex flex-col gap-0.5">
        <p
          data-testid="activity-card-sentence"
          className="text-signal-sm text-text-primary"
        >
          <span
            data-testid="activity-card-subject"
            data-subject-type={subject.type}
            className="font-semibold text-text-primary"
          >
            {subject.name}
          </span>{' '}
          <span
            data-testid="activity-card-action"
            className="text-text-secondary"
          >
            {action}
          </span>{' '}
          <span
            data-testid="activity-card-object"
            data-object-type={object.type}
            className="font-semibold text-text-primary"
          >
            {object.name}
          </span>
          {indirectObject !== undefined ? (
            <>
              {' '}
              <span
                data-testid="activity-card-indirect-arrow"
                aria-hidden="true"
                className="text-text-tertiary"
              >
                {'->'}
              </span>{' '}
              <span
                data-testid="activity-card-indirect-object"
                data-indirect-object-type={indirectObject.type}
                className="font-semibold text-text-primary"
              >
                {indirectObject.name}
              </span>
            </>
          ) : null}
        </p>
        <span className="text-signal-xs uppercase tracking-wide text-text-tertiary">
          {ACTIVITY_TYPE_LABELS[activityType]}
        </span>
      </div>
    </article>
  );
}
